import { CalendarEventRef } from '@griply/types';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { getExternalCalendarEvent } from 'shared/services/backend-api';

import { queryKey } from './use-external-calendar-event-query';

const STALE_TIME_MS = 60 * 1000;

// The events *list* cannot answer "does this repeat?": a provider returns each occurrence
// expanded, and the recurrence rule lives on the series master, so every instance arrives
// with `recurrence` absent. The detail endpoint resolves the master and reports
// `isRecurring` — this asks it, reusing the same cache entry the popup fills, so a drag on
// an event whose popup was already opened costs nothing.
export const useResolveExternalEventRecurrence = () => {
  const client = useQueryClient();

  return useCallback(
    async (eventRef: CalendarEventRef): Promise<boolean> => {
      try {
        const details = await client.fetchQuery({
          queryKey: [
            queryKey,
            eventRef.connectionId,
            eventRef.calendarId,
            eventRef.eventId,
          ],
          queryFn: async () => getExternalCalendarEvent(eventRef),
          staleTime: STALE_TIME_MS,
        });
        return !!details?.isRecurring;
      } catch {
        // Unreachable detail: fall through as non-recurring. Single is the conservative
        // answer — it touches one occurrence instead of rewriting a whole series.
        return false;
      }
    },
    [client],
  );
};
